import { Stack } from '@mui/material'
import { SPACING } from '@common/theme/spacing'
import QueriesTable from './QueriesTable'
import QueriesTableSkeleton from './QueriesTableSkeleton'
import CreateQueryDrawer from './CreateQueryDrawer'
import ViewOrEditQueryDrawer from './ViewOrEditQueryDrawer'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueriesManagementHandlers } from '../hooks/useQueriesManagementHandlers'
import { StudyQueryListSchema } from '@common/config/api/client'
import { useEffect } from 'react'
import { generatePathWithParams } from '@common/utils/urlUtils'
import { ROUTES } from 'src/routes'

export default function Queries() {
  const { studyId, queryId } = useParams<{ studyId: string; queryId?: string }>()
  const navigate = useNavigate()

  const {
    page,
    queryListParams,
    isCreateDrawerOpen,
    handleCreateQueryClick,
    handleCloseCreateDrawer,
    handleSubmitCreateQuery,
    isViewDrawerOpen,
    selectedQueryDetails,
    isLoadingDetails,
    isLoadingMetadata,
    isErrorDetails,
    handleViewQueryClick,
    handleCloseViewDrawer,
    queriesResponse,
    isLoadingList,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleRequestSort,
    handleUpdateStatus,
    handleSendMessage,
    handleUpdateQuery,
    isUpdatingStatus,
    isSendingMessage,
    isUpdatingQuery,
  } = useQueriesManagementHandlers()

  const queriesPath = generatePathWithParams(ROUTES.QUERIES.path, { studyId })

  useEffect(() => {
    if (queryId && !isViewDrawerOpen) {
      handleViewQueryClick(queryId)
    }
  }, [queryId, isViewDrawerOpen, handleViewQueryClick])

  const handleDrawerClose = () => {
    handleCloseViewDrawer()
    if (queryId) {
      navigate(queriesPath)
    }
  }

  if (!studyId) {
    return <div>Loading study information...</div>
  }

  return (
    <>
      <Stack spacing={SPACING.sectionsSpacing}>
        {isLoadingList ? (
          <QueriesTableSkeleton />
        ) : (
          <QueriesTable
            queries={queriesResponse!}
            page={page}
            orderBy={queryListParams.order_by_field as keyof StudyQueryListSchema}
            order={queryListParams.asc_order ? 'asc' : 'desc'}
            searchValue={queryListParams.search ?? ''}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSearch={handleSearch}
            onRequestSort={handleRequestSort}
            onCreateClick={handleCreateQueryClick}
            onViewClick={(id) => {
              const quertDetailsPath = generatePathWithParams(ROUTES.QUERY_DETAILS.path, { studyId, queryId: id })
              navigate(quertDetailsPath)
            }}
          />
        )}
      </Stack>

      <CreateQueryDrawer
        studyId={studyId}
        open={isCreateDrawerOpen}
        onClose={handleCloseCreateDrawer}
        onSubmit={handleSubmitCreateQuery}
      />

      {(isViewDrawerOpen || isLoadingDetails || isLoadingMetadata) && (
        <ViewOrEditQueryDrawer
          open={isViewDrawerOpen}
          onClose={handleDrawerClose}
          query={selectedQueryDetails!}
          isLoading={isLoadingDetails}
          isError={isErrorDetails}
          onUpdateStatus={handleUpdateStatus}
          onSendMessage={handleSendMessage}
          onUpdateQuery={handleUpdateQuery}
          isUpdatingStatus={isUpdatingStatus}
          isSendingMessage={isSendingMessage}
          isUpdatingQuery={isUpdatingQuery}
        />
      )}
    </>
  )
}
